'use client';
import React, { useState } from 'react';
import { Bell, AlertCircle, Calendar, CreditCard, Briefcase, Filter, Check, X, ChevronDown, Loader2, UserCheck, UserX, ShieldCheck } from 'lucide-react';
import { useNotifications } from '@/lib/hooks/useNotifications';
import type { AppNotification } from '@/lib/hooks/useNotifications';

type NotifType = 'all' | 'job' | 'booking' | 'payout' | 'complaint' | 'onboarding';
type ReadFilter = 'all' | 'unread' | 'read';

const customerTypeFilters: { id: NotifType; label: string; icon: React.ElementType }[] = [
  { id: 'all', label: 'All', icon: Bell },
  { id: 'job', label: 'Job Updates', icon: Briefcase },
  { id: 'booking', label: 'Bookings', icon: Calendar },
  { id: 'payout', label: 'Payouts', icon: CreditCard },
  { id: 'complaint', label: 'Complaints', icon: AlertCircle },
];

const cleanerTypeFilters: { id: NotifType; label: string; icon: React.ElementType }[] = [
  { id: 'all', label: 'All', icon: Bell },
  { id: 'job', label: 'Job Updates', icon: Briefcase },
  { id: 'payout', label: 'Payouts', icon: CreditCard },
  { id: 'complaint', label: 'Complaints', icon: AlertCircle },
  { id: 'onboarding', label: 'Onboarding', icon: ShieldCheck },
];

const adminTypeFilters: { id: NotifType; label: string; icon: React.ElementType }[] = [
  { id: 'all', label: 'All', icon: Bell },
  { id: 'job', label: 'Job Updates', icon: Briefcase },
  { id: 'payout', label: 'Payouts', icon: CreditCard },
  { id: 'complaint', label: 'Complaints', icon: AlertCircle },
  { id: 'onboarding', label: 'Onboarding', icon: ShieldCheck },
  { id: 'booking', label: 'Bookings', icon: Calendar },
];

const typeIconMap: Record<string, React.ElementType> = {
  job: Briefcase,
  booking: Calendar,
  payout: CreditCard,
  complaint: AlertCircle,
  onboarding: ShieldCheck,
};

const typeColorMap: Record<string, string> = {
  job: 'text-primary bg-primary/10',
  booking: 'text-info bg-info-bg',
  payout: 'text-success bg-success-bg',
  complaint: 'text-warning bg-warning-bg',
  onboarding: 'text-purple-600 bg-purple-50',
};

const typeBadgeMap: Record<string, string> = {
  job: 'bg-primary/10 text-primary',
  booking: 'bg-info-bg text-info',
  payout: 'bg-success-bg text-success',
  complaint: 'bg-warning-bg text-warning',
  onboarding: 'bg-purple-50 text-purple-600',
};

const typeLabel: Record<string, string> = {
  job: 'Job',
  booking: 'Booking',
  payout: 'Payout',
  complaint: 'Complaint',
  onboarding: 'Onboarding',
};

function formatTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} min${mins !== 1 ? 's' : ''} ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs !== 1 ? 's' : ''} ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days} day${days !== 1 ? 's' : ''} ago`;
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function groupByDate(notifications: AppNotification[]): { label: string; items: AppNotification[] }[] {
  const groups: Record<string, AppNotification[]> = {};
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  notifications.forEach((n) => {
    const d = new Date(n.createdAt);
    d.setHours(0, 0, 0, 0);
    let label: string;
    if (d.getTime() === today.getTime()) {
      label = 'Today';
    } else if (d.getTime() === yesterday.getTime()) {
      label = 'Yesterday';
    } else {
      label = new Date(n.createdAt).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
    }
    if (!groups[label]) groups[label] = [];
    groups[label].push(n);
  });

  return Object.entries(groups).map(([label, items]) => ({ label, items }));
}

interface NotificationsContentProps {
  role: 'customer' | 'cleaner' | 'admin';
}

export default function NotificationsContent({ role }: NotificationsContentProps) {
  const { notifications, loading, error, unreadCount, markRead, markAllRead, dismiss } =
    useNotifications(role);

  const [typeFilter, setTypeFilter] = useState<NotifType>('all');
  const [readFilter, setReadFilter] = useState<ReadFilter>('all');
  const [showReadDropdown, setShowReadDropdown] = useState(false);

  const typeFilters =
    role === 'admin' ? adminTypeFilters : role === 'cleaner' ? cleanerTypeFilters : customerTypeFilters;

  const filtered = notifications.filter((n) => {
    const matchType = typeFilter === 'all' || n.type === typeFilter;
    const matchRead =
      readFilter === 'all' ||
      (readFilter === 'unread' ? !n.isRead : n.isRead);
    return matchType && matchRead;
  });

  const grouped = groupByDate(filtered);

  const readFilterLabels: Record<ReadFilter, string> = {
    all: 'All Status',
    unread: 'Unread',
    read: 'Read',
  };

  const roleDescriptions: Record<string, string> = {
    customer: 'Stay up to date with your cleaning activity.',
    cleaner: 'Track your job assignments, payouts, and onboarding status.',
    admin: 'Monitor all job updates, payouts, complaints, and onboarding decisions.',
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold text-foreground flex items-center gap-2">
            Notifications
            {unreadCount > 0 && (
              <span className="bg-danger text-white text-xs font-bold px-2 py-0.5 rounded-full">
                {unreadCount}
              </span>
            )}
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">{roleDescriptions[role]}</p>
        </div>
        {unreadCount > 0 && (
          <button
            onClick={markAllRead}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-border bg-card text-sm font-semibold text-foreground hover:bg-muted transition-all"
          >
            <Check size={15} /> Mark all as read
          </button>
        )}
      </div>

      {/* Stats row for admin */}
      {role === 'admin' && !loading && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {adminTypeFilters.filter(f => f.id !== 'all').map((f) => {
            const count = notifications.filter((n) => n.type === f.id).length;
            const unread = notifications.filter((n) => n.type === f.id && !n.isRead).length;
            return (
              <button
                key={f.id}
                onClick={() => setTypeFilter(f.id)}
                className={`bg-card border rounded-xl p-3 text-left transition-all hover:border-primary/40 ${
                  typeFilter === f.id ? 'border-primary bg-primary/5' : 'border-border'
                }`}
              >
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center mb-2 ${typeColorMap[f.id]}`}>
                  <f.icon size={13} />
                </div>
                <p className="text-lg font-extrabold text-foreground">{count}</p>
                <p className="text-xs text-muted-foreground">{f.label}</p>
                {unread > 0 && (
                  <span className="mt-1 inline-block bg-danger text-white text-xs font-bold px-1.5 py-0.5 rounded-full">
                    {unread} new
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Type filter pills */}
        <div className="flex gap-1.5 flex-wrap flex-1">
          {typeFilters.map((f) => {
            const count =
              f.id === 'all'
                ? notifications.length
                : notifications.filter((n) => n.type === f.id).length;
            return (
              <button
                key={f.id}
                onClick={() => setTypeFilter(f.id)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold transition-all duration-150 ${
                  typeFilter === f.id
                    ? 'bg-primary text-white' :'bg-card border border-border text-muted-foreground hover:text-foreground hover:border-primary/40'
                }`}
              >
                <f.icon size={12} />
                {f.label}
                <span
                  className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${
                    typeFilter === f.id ? 'bg-white/20' : 'bg-muted'
                  }`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Read status dropdown */}
        <div className="relative">
          <button
            onClick={() => setShowReadDropdown(!showReadDropdown)}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-card border border-border text-xs font-semibold text-muted-foreground hover:text-foreground hover:border-primary/40 transition-all"
          >
            <Filter size={12} />
            {readFilterLabels[readFilter]}
            <ChevronDown size={12} />
          </button>
          {showReadDropdown && (
            <div className="absolute right-0 top-full mt-1 bg-card border border-border rounded-xl shadow-lg z-10 overflow-hidden min-w-[140px]">
              {(['all', 'unread', 'read'] as ReadFilter[]).map((opt) => (
                <button
                  key={opt}
                  onClick={() => {
                    setReadFilter(opt);
                    setShowReadDropdown(false);
                  }}
                  className={`w-full text-left px-4 py-2.5 text-sm font-semibold transition-colors ${
                    readFilter === opt
                      ? 'text-primary bg-primary/5' :'text-foreground hover:bg-muted'
                  }`}
                >
                  {readFilterLabels[opt]}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Loading state */}
      {loading && (
        <div className="bg-card rounded-2xl border border-border card-shadow py-16 flex flex-col items-center gap-3">
          <Loader2 size={28} className="text-primary animate-spin" />
          <p className="text-sm text-muted-foreground">Loading notifications…</p>
        </div>
      )}

      {/* Error state */}
      {!loading && error && (
        <div className="bg-card rounded-2xl border border-border card-shadow py-10 text-center">
          <AlertCircle size={28} className="text-danger mx-auto mb-2" />
          <p className="text-sm font-semibold text-foreground">Failed to load notifications</p>
          <p className="text-xs text-muted-foreground mt-1">{error}</p>
        </div>
      )}

      {/* Notifications list — grouped by date */}
      {!loading && !error && (
        <div className="space-y-4">
          {filtered.length === 0 ? (
            <div className="bg-card rounded-2xl border border-border card-shadow py-16 text-center">
              <Bell size={32} className="text-muted-foreground mx-auto mb-3" />
              <p className="text-sm font-semibold text-foreground">No notifications found</p>
              <p className="text-xs text-muted-foreground mt-1">Try adjusting your filters.</p>
            </div>
          ) : (
            grouped.map(({ label, items }) => (
              <div key={label}>
                {/* Date group header */}
                <div className="flex items-center gap-3 mb-2">
                  <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider whitespace-nowrap">{label}</p>
                  <div className="flex-1 h-px bg-border" />
                  <span className="text-xs text-muted-foreground">{items.length}</span>
                </div>

                <div className="bg-card rounded-2xl border border-border card-shadow overflow-hidden">
                  <div className="divide-y divide-border">
                    {items.map((notif: AppNotification) => {
                      const IconComp = typeIconMap[notif.type] ?? Bell;
                      const iconColor = typeColorMap[notif.type] ?? 'text-primary bg-primary/10';
                      const badgeColor = typeBadgeMap[notif.type] ?? 'bg-primary/10 text-primary';
                      let label = typeLabel[notif.type] ?? notif.type;

                      // Determine special icon for onboarding approved/rejected
                      let SpecialIcon: React.ElementType | null = null;
                      if (notif.type === 'onboarding') {
                        if (notif.title?.toLowerCase().includes('approved')) SpecialIcon = UserCheck;
                        else if (notif.title?.toLowerCase().includes('rejected') || notif.title?.toLowerCase().includes('declined')) SpecialIcon = UserX;
                      }

                      return (
                        <div
                          key={notif.id}
                          className={`flex items-start gap-4 px-5 py-4 transition-colors ${
                            !notif.isRead ? 'bg-primary/[0.03]' : 'hover:bg-muted/20'
                          }`}
                        >
                          {/* Unread dot */}
                          <div className="flex-shrink-0 mt-2.5">
                            <div
                              className={`w-2 h-2 rounded-full ${
                                !notif.isRead ? 'bg-primary' : 'bg-transparent'
                              }`}
                            />
                          </div>

                          {/* Icon */}
                          <div
                            className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5 ${iconColor}`}
                          >
                            {SpecialIcon ? <SpecialIcon size={16} /> : <IconComp size={16} />}
                          </div>

                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap mb-0.5">
                                  <p
                                    className={`text-sm font-bold ${
                                      !notif.isRead ? 'text-foreground' : 'text-foreground/80'
                                    }`}
                                  >
                                    {notif.title}
                                  </p>
                                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${badgeColor}`}>
                                    {label}
                                  </span>
                                </div>
                                <p className="text-xs text-muted-foreground leading-relaxed">
                                  {notif.message}
                                </p>
                                {notif.metadata?.job_id && (
                                  <p className="text-xs text-muted-foreground mt-1">
                                    Job #{String(notif.metadata.job_id).slice(0, 8)}
                                  </p>
                                )}
                              </div>
                              <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                                <span className="text-xs text-muted-foreground whitespace-nowrap">
                                  {formatTime(notif.createdAt)}
                                </span>
                                <div className="flex items-center gap-1">
                                  {!notif.isRead && (
                                    <button
                                      onClick={() => markRead(notif.id)}
                                      className="p-1 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-all"
                                      aria-label="Mark as read"
                                    >
                                      <Check size={13} />
                                    </button>
                                  )}
                                  <button
                                    onClick={() => dismiss(notif.id)}
                                    className="p-1 rounded-lg text-muted-foreground hover:text-danger hover:bg-danger/10 transition-all"
                                    aria-label="Dismiss"
                                  >
                                    <X size={13} />
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
